import type { ImportKind } from '@nucleus-commerce/api-client/admin';

/**
 * CSV columns per import kind. Header names are data (they must match the file), so they are not
 * translated. Each required entry is a group of alternatives: inventory rows need a SKU or a
 * Variant ID. The backend matches headers case-insensitively.
 */
export interface ColumnGuide {
  required: ReadonlyArray<readonly string[]>;
  optional: readonly string[];
  /** Also reads one `Attribute <key>` column per attribute, the layout the product export writes. */
  attributeColumns?: boolean;
  /** Reads `Variant Weight` in the unit `Variant Weight Unit` names, before `Variant Grams`. */
  weightUnits?: boolean;
}

/** Header prefix of an attribute column; the rest of the header is the attribute key. */
export const ATTRIBUTE_COLUMN_PREFIX = 'Attribute ';

const OPTIONS = [1, 2, 3].flatMap((n) => [`Option${n} Name`, `Option${n} Value`]);
/** Units `Variant Weight Unit` accepts; the weight is kept in that unit (D-141). */
export const WEIGHT_UNIT_CODES = 'g, kg, dwt, ozt, oz, lb';
/** Make the product a diamond setting: shapes and stone types as lists, carat bounds as numbers. */
const CONFIGURATOR = [
  'Configurator Shapes',
  'Configurator Stone Types',
  'Configurator Carat Min',
  'Configurator Carat Max',
];

export const COLUMN_GUIDES: Record<ImportKind, ColumnGuide> = {
  products_csv: {
    required: [['Handle'], ['Title']],
    optional: [
      'Body (HTML)',
      'Vendor',
      'Type',
      'Tags',
      'Published',
      ...OPTIONS,
      'Variant SKU',
      'Variant Grams',
      'Variant Weight',
      'Variant Weight Unit',
      'Variant Price',
      'Variant Compare At Price',
      'Variant Inventory Tracker',
      'Variant Inventory Qty',
      'Variant Requires Shipping',
      'Variant Taxable',
      'Fulfillment Mode',
      'Lead Time Min',
      'Lead Time Max',
      'Requires Design Approval',
      'Track Inventory',
      ...CONFIGURATOR,
      'Variant Cost',
      'Image Src',
      'Image Position',
      'Image Alt Text',
      'Variant Image',
    ],
    attributeColumns: true,
    weightUnits: true,
  },
  customers_csv: {
    required: [['Email']],
    optional: ['First Name', 'Last Name', 'Phone', 'Tags', 'Accepts Marketing', 'Notes'],
  },
  inventory_csv: {
    required: [['SKU', 'Variant ID'], ['Quantity']],
    optional: ['Location'],
  },
};

/** The backend reports a group by its lower-cased names joined with " or ". */
const groupKey = (group: readonly string[]) => group.map((name) => name.toLowerCase()).join(' or ');

/**
 * Maps `meta.missing` of a 422 ("handle", "sku or variant id") back to the guide's spelling so
 * the message names the columns the way the guide shows them. Unknown names pass through.
 */
export function missingColumnLabels(
  kind: ImportKind,
  missing: readonly string[],
  or: string,
): string[] {
  const groups = COLUMN_GUIDES[kind].required;
  return missing.map((name) => {
    const group = groups.find((g) => groupKey(g) === name.trim().toLowerCase());
    return group ? group.join(` ${or} `) : name;
  });
}
